import { accountsApiRequest } from "../lib/endpoints";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import "./accounts-page.css";

export type AccountsApiRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

export interface AccountsPageProps {
  /** Reuse the Gateway's existing authenticated API helper. */
  apiRequest?: AccountsApiRequest;
}

async function defaultApiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return accountsApiRequest<T>(path, init);
}
type LocalAccount = {
  id: string;
  externalAccountId: string;
  accountNumber?: string | null;
  name: string;
  fullyQualifiedName?: string | null;
  accountType?: string | null;
  accountSubType?: string | null;
  classification?: string | null;
  currencyCode?: string | null;
  isSubAccount: boolean;
  parentExternalAccountId?: string | null;
  active: boolean;
  missingSinceUtc?: string | null;
  isEligibleForNewPosting: boolean;
  allowAiLearningEnabled: boolean;
  syncToken?: string | null;
  sourceLastUpdatedUtc?: string | null;
  firstSeenUtc: string;
  lastSeenUtc: string;
};

type SetAiLearningConsentResponse = { updated: number };

// QuickBooks' own Classification values, grouped balance-sheet-first: what the company owns and
// owes (Asset, Liability, Equity) before what it earned and spent (Revenue, Expense). Anything
// unrecognized sorts last rather than crashing the comparator.
const CLASSIFICATION_RANK: Record<string, number> = {
  Asset: 0,
  Liability: 1,
  Equity: 2,
  Revenue: 3,
  Income: 3,
  Expense: 4,
};

function classificationRank(value?: string | null): number {
  if (!value) return 99;
  return CLASSIFICATION_RANK[value] ?? 98;
}

type SortKey =
  | "accountNumber"
  | "name"
  | "accountType"
  | "classification"
  | "currencyCode"
  | "active"
  | "isEligibleForNewPosting"
  | "allowAiLearningEnabled"
  | "sourceLastUpdatedUtc"
  | "externalAccountId";

function compareValues(a: LocalAccount, b: LocalAccount, key: SortKey): number {
  if (key === "classification") {
    const rank = classificationRank(a.classification) - classificationRank(b.classification);
    if (rank !== 0) return rank;
    return a.name.localeCompare(b.name);
  }
  if (key === "active" || key === "isEligibleForNewPosting" || key === "allowAiLearningEnabled") {
    return Number(b[key]) - Number(a[key]);
  }
  const av = a[key] ?? "";
  const bv = b[key] ?? "";
  return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
}

type PagedResult<T> = {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

type CoaSnapshot = {
  id: string;
  connectorInstanceId: string;
  version: number;
  capturedUtc: string;
  accountCount: number;
  activeAccountCount: number;
  eligibleAccountCount: number;
  contentHash: string;
  state: string | number;
  publishedUtc?: string | null;
};

type CoaSyncResponse = {
  success: boolean;
  complete: boolean;
  usedFullPull: boolean;
  checkpointUtc?: string | null;
  effectiveSinceUtc?: string | null;
  recordsTransferred: number;
  recordsCreated: number;
  recordsUpdated: number;
  recordsDeactivated: number;
  recordsSkippedUnchanged: number;
  snapshotId?: string | null;
  snapshotVersion?: number | null;
  snapshotChanged: boolean;
  completedUtc: string;
  message?: string | null;
  errorCode?: string | null;
};

type CoaPullResponse = {
  success: boolean;
  complete: boolean;
  recordsPulled: number;
  recordsCreated: number;
  recordsUpdated: number;
  recordsDeactivated: number;
  recordsNoLongerReported: number;
  snapshotId?: string | null;
  snapshotVersion?: number | null;
  snapshotChanged: boolean;
  completedUtc: string;
  message?: string | null;
  errorCode?: string | null;
};

type Filter = "all" | "active" | "inactive" | "eligible";

function fmtDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function sourceStatus(account: LocalAccount): { label: string; tone: string } {
  if (account.missingSinceUtc) return { label: "Not reported", tone: "warning" };
  if (!account.active) return { label: "Inactive", tone: "muted" };
  return { label: "Active", tone: "success" };
}

function postingStatus(account: LocalAccount): { label: string; tone: string } {
  return account.isEligibleForNewPosting
    ? { label: "Eligible", tone: "success" }
    : { label: "Historical only", tone: "muted" };
}

function SortHeader({
  label,
  sortKey,
  current,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: "asc" | "desc";
  onSort: (key: SortKey) => void;
}) {
  const active = current === sortKey;
  return (
    <th aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={active ? "coa-sort-btn active" : "coa-sort-btn"} onClick={() => onSort(sortKey)}>
        {label}
        <span className="coa-sort-arrow">{active ? (dir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export default function AccountsPage({ apiRequest = defaultApiRequest }: AccountsPageProps = {}) {
  const [rows, setRows] = useState<PagedResult<LocalAccount>>({
    items: [],
    page: 1,
    pageSize: 100,
    totalCount: 0,
    totalPages: 0,
  });
  const [snapshot, setSnapshot] = useState<CoaSnapshot | null>(null);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState<"sync" | "full" | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Client-side sort of whatever page is loaded - clicking a header toggles direction, clicking a
  // different header switches to it ascending. Classification-first (Balance Sheet, then Income
  // Statement) is the default until someone picks something else.
  const [sortKey, setSortKey] = useState<SortKey>("classification");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [consentBusyId, setConsentBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Row selection: click picks one, ctrl/cmd-click adds or removes one without touching the rest,
  // shift-click extends from the last row touched through the clicked one - the same three-gesture
  // convention as a file manager, so a 4,000-account chart never means 4,000 individual clicks to
  // turn AI posting on for one department.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedItems = useMemo(() => {
    const sorted = [...rows.items].sort((a, b) => compareValues(a, b, sortKey));
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [rows.items, sortKey, sortDir]);

  const toggleRowCheckbox = (index: number, event: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) => {
    const id = sortedItems[index].id;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (event.shiftKey && lastSelectedIndex !== null) {
        const [from, to] = [lastSelectedIndex, index].sort((a, b) => a - b);
        for (let i = from; i <= to; i += 1) next.add(sortedItems[i].id);
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setLastSelectedIndex(index);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  };

  async function bulkSetAiPermitted(enabled: boolean) {
    const targets = sortedItems.filter((a) => selectedIds.has(a.id));
    if (targets.length === 0) return;
    setBulkBusy(true);
    setError(null);
    const externalAccountIds = targets.map((a) => a.externalAccountId);
    setRows((current) => ({
      ...current,
      items: current.items.map((item) =>
        selectedIds.has(item.id) ? { ...item, allowAiLearningEnabled: enabled } : item,
      ),
    }));
    try {
      await apiRequest<SetAiLearningConsentResponse>("/api/learning/accounts/ai-learning", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ externalAccountIds, enabled }),
      });
      clearSelection();
    } catch (e) {
      setRows((current) => ({
        ...current,
        items: current.items.map((item) =>
          selectedIds.has(item.id) ? { ...item, allowAiLearningEnabled: !enabled } : item,
        ),
      }));
      setError(e instanceof Error ? e.message : "AI-permitted-posting consent could not be saved for the selection.");
    } finally {
      setBulkBusy(false);
    }
  }

  async function toggleAiLearning(account: LocalAccount) {
    const nextEnabled = !account.allowAiLearningEnabled;
    setConsentBusyId(account.id);
    setError(null);
    // Optimistic: a reviewer flipping one switch shouldn't wait on a round trip to see it move.
    setRows((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === account.id ? { ...item, allowAiLearningEnabled: nextEnabled } : item,
      ),
    }));
    try {
      await apiRequest<SetAiLearningConsentResponse>("/api/learning/accounts/ai-learning", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          externalAccountIds: [account.externalAccountId],
          enabled: nextEnabled,
        }),
      });
    } catch (e) {
      // Roll back on failure so the switch never lies about what actually saved.
      setRows((current) => ({
        ...current,
        items: current.items.map((item) =>
          item.id === account.id ? { ...item, allowAiLearningEnabled: !nextEnabled } : item,
        ),
      }));
      setError(e instanceof Error ? e.message : "AI-learning consent could not be saved.");
    } finally {
      setConsentBusyId(null);
    }
  }

  const queryString = useMemo(() => {
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("pageSize", "100");
    if (search.trim()) qs.set("search", search.trim());
    if (filter === "active") qs.set("active", "true");
    if (filter === "inactive") qs.set("active", "false");
    if (filter === "eligible") qs.set("eligibleOnly", "true");
    return qs.toString();
  }, [filter, page, search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiRequest<PagedResult<LocalAccount>>(
        `/api/learning/accounts?${queryString}`,
      );
      setRows(list);

      try {
        const current = await apiRequest<CoaSnapshot>("/api/learning/accounts/snapshot");
        setSnapshot(current);
      } catch {
        // A 404 here simply means the first Chart of Accounts pull has not happened yet.
        setSnapshot(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "The Chart of Accounts could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  async function checkChanges() {
    setBusy("sync");
    setError(null);
    setNotice(null);
    try {
      const result = await apiRequest<CoaSyncResponse>("/api/learning/accounts/sync", {
        method: "POST",
      });
      setNotice(
        result.message ??
          `Transferred ${result.recordsTransferred} source row(s); ${result.recordsCreated + result.recordsUpdated} changed locally.`,
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "QuickBooks change check failed.");
    } finally {
      setBusy(null);
    }
  }

  async function fullReconcile() {
    if (
      !window.confirm(
        "Full reconcile reads the entire QuickBooks Chart of Accounts, including inactive accounts. Use it for first setup or reconciliation, not routine refresh. Continue?",
      )
    ) {
      return;
    }

    setBusy("full");
    setError(null);
    setNotice(null);
    try {
      const result = await apiRequest<CoaPullResponse>("/api/learning/accounts/pull", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      setNotice(result.message ?? `Full reconcile read ${result.recordsPulled} account(s).`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Full Chart of Accounts reconcile failed.");
    } finally {
      setBusy(null);
    }
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setSearch(searchDraft);
  }

  const inactiveCount = snapshot ? Math.max(0, snapshot.accountCount - snapshot.activeAccountCount) : null;

  return (
    <main className="coa-page">
      <div className="coa-heading-row">
        <div>
          <h1>Chart of Accounts</h1>
          <p>
            The complete local QuickBooks Chart of Accounts used for posting validation and historical accounting evidence.
            Inactive accounts remain visible so older postings can still resolve correctly.
          </p>
        </div>
        <div className="coa-actions">
          <button className="coa-btn coa-btn-secondary" onClick={fullReconcile} disabled={busy !== null}>
            {busy === "full" ? "Reconciling…" : "Full reconcile"}
          </button>
          <button className="coa-btn coa-btn-primary" onClick={checkChanges} disabled={busy !== null}>
            {busy === "sync" ? "Checking…" : "Check QuickBooks for changes"}
          </button>
        </div>
      </div>

      <section className="coa-sync-note">
        <strong>Low-volume sync:</strong> normal checks request only accounts modified after the last completed COA checkpoint.
        A full-chart read is reserved for first setup or reconciliation.
      </section>

      {error && <div className="coa-alert coa-alert-error">{error}</div>}
      {notice && <div className="coa-alert coa-alert-info">{notice}</div>}

      <section className="coa-stats" aria-label="Chart of Accounts summary">
        <div className="coa-stat"><span>Total accounts</span><strong>{snapshot?.accountCount ?? "—"}</strong></div>
        <div className="coa-stat"><span>Active</span><strong>{snapshot?.activeAccountCount ?? "—"}</strong></div>
        <div className="coa-stat"><span>Inactive</span><strong>{inactiveCount ?? "—"}</strong></div>
        <div className="coa-stat"><span>Eligible for new posting</span><strong>{snapshot?.eligibleAccountCount ?? "—"}</strong></div>
        <div className="coa-stat coa-stat-wide">
          <span>Governed snapshot</span>
          <strong>{snapshot ? `v${snapshot.version}` : "Not captured"}</strong>
          <small>{snapshot ? fmtDate(snapshot.capturedUtc) : "Run the first Chart of Accounts pull."}</small>
        </div>
      </section>

      <section className="coa-panel">
        <div className="coa-toolbar">
          <form className="coa-search" onSubmit={submitSearch}>
            <label htmlFor="coa-search-input">Search</label>
            <div className="coa-search-row">
              <input
                id="coa-search-input"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder="Account number, name, fully qualified name or type"
              />
              <button className="coa-btn coa-btn-secondary" type="submit">Search</button>
            </div>
          </form>

          <div className="coa-filters" role="group" aria-label="Account status filter">
            {(["all", "active", "inactive", "eligible"] as Filter[]).map((value) => (
              <button
                key={value}
                type="button"
                className={filter === value ? "coa-filter active" : "coa-filter"}
                onClick={() => {
                  setFilter(value);
                  setPage(1);
                }}
              >
                {value === "all" ? "All" : value === "eligible" ? "Eligible for posting" : value[0].toUpperCase() + value.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="coa-empty">Loading Chart of Accounts…</div>
        ) : rows.items.length === 0 ? (
          <div className="coa-empty">
            <strong>No Chart of Accounts cached</strong>
            <span>Run the first full reconcile to load active and inactive QuickBooks accounts.</span>
            <button className="coa-btn coa-btn-primary" onClick={fullReconcile} disabled={busy !== null}>
              Pull Chart of Accounts
            </button>
          </div>
        ) : (
          <>
            {selectedIds.size > 0 ? (
              <div className="coa-bulk-bar">
                <strong>{selectedIds.size} selected</strong>
                <button type="button" className="coa-btn coa-btn-secondary" disabled={bulkBusy} onClick={() => void bulkSetAiPermitted(true)}>
                  {bulkBusy ? "Working…" : "Turn AI Permitted on"}
                </button>
                <button type="button" className="coa-btn coa-btn-secondary" disabled={bulkBusy} onClick={() => void bulkSetAiPermitted(false)}>
                  {bulkBusy ? "Working…" : "Turn AI Permitted off"}
                </button>
                <button type="button" className="coa-btn coa-btn-secondary" disabled={bulkBusy} onClick={clearSelection}>
                  Clear selection
                </button>
              </div>
            ) : null}
            <div className="coa-table-wrap">
              <table className="coa-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all accounts on this page"
                        checked={sortedItems.length > 0 && sortedItems.every((a) => selectedIds.has(a.id))}
                        ref={(el) => {
                          if (!el) return;
                          const some = sortedItems.some((a) => selectedIds.has(a.id));
                          const all = sortedItems.length > 0 && sortedItems.every((a) => selectedIds.has(a.id));
                          el.indeterminate = some && !all;
                        }}
                        onChange={(e) =>
                          setSelectedIds(e.target.checked ? new Set(sortedItems.map((a) => a.id)) : new Set())
                        }
                      />
                    </th>
                    <SortHeader label="Account no." sortKey="accountNumber" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Account" sortKey="name" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Type / subtype" sortKey="accountType" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Classification" sortKey="classification" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Currency" sortKey="currencyCode" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Status" sortKey="active" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Posting" sortKey="isEligibleForNewPosting" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="AI Permitted" sortKey="allowAiLearningEnabled" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="QuickBooks updated" sortKey="sourceLastUpdatedUtc" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortHeader label="QBO ID" sortKey="externalAccountId" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((account, index) => {
                    const status = sourceStatus(account);
                    const posting = postingStatus(account);
                    const selected = selectedIds.has(account.id);
                    return (
                      <tr key={account.id} className={selected ? "coa-row-selected" : undefined}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected}
                            aria-label={`Select ${account.name}`}
                            onClick={(e) => toggleRowCheckbox(index, e)}
                            onChange={() => {
                              /* selection state is driven by onClick, which carries shift/ctrl */
                            }}
                          />
                        </td>
                        <td className="coa-mono">{account.accountNumber || "—"}</td>
                        <td>
                          <strong>{account.name}</strong>
                          <small>{account.fullyQualifiedName && account.fullyQualifiedName !== account.name ? account.fullyQualifiedName : ""}</small>
                        </td>
                        <td>
                          <span>{account.accountType || "—"}</span>
                          <small>{account.accountSubType || ""}</small>
                        </td>
                        <td>{account.classification || "—"}</td>
                        <td>{account.currencyCode || "—"}</td>
                        <td><span className={`coa-badge ${status.tone}`}>{status.label}</span></td>
                        <td><span className={`coa-badge ${posting.tone}`}>{posting.label}</span></td>
                        <td>
                          <label className="coa-toggle-cell">
                            <span className="coa-toggle">
                              <input
                                type="checkbox"
                                checked={account.allowAiLearningEnabled}
                                disabled={consentBusyId === account.id}
                                aria-label={`AI permitted posting for ${account.name}`}
                                onChange={() => void toggleAiLearning(account)}
                              />
                              <span className="coa-toggle-track" />
                            </span>
                            {account.allowAiLearningEnabled ? "On" : "Off"}
                          </label>
                        </td>
                        <td>{fmtDate(account.sourceLastUpdatedUtc)}</td>
                        <td className="coa-mono">{account.externalAccountId}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="coa-pagination">
          <span>
            {rows.totalCount === 0 ? "0 accounts" : `${rows.totalCount.toLocaleString()} account${rows.totalCount === 1 ? "" : "s"}`}
          </span>
          <div>
            <button className="coa-btn coa-btn-secondary" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <span>Page {rows.page} of {Math.max(1, rows.totalPages)}</span>
            <button className="coa-btn coa-btn-secondary" disabled={page >= rows.totalPages || loading} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
